import {
  type CSSProperties,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

// ─── Types ───

export interface DrawerProps {
  /** Whether the drawer is open */
  open: boolean;
  /** Callback when open state changes */
  onOpenChange: (open: boolean) => void;
  /** Drawer title rendered in the header */
  title?: string;
  /** Optional description below the title */
  description?: string;
  /** Drawer width — default 460px (from Figma spec) */
  width?: number | string;
  /** Drawer body content */
  children: ReactNode;
  /** Optional sticky footer content */
  footer?: ReactNode;
  /** Optional badges/tags rendered below the title */
  badges?: ReactNode;
  /** Close on backdrop click — default true */
  closeOnBackdrop?: boolean;
  /** Close on Escape key — default true */
  closeOnEsc?: boolean;
  /** Show close (X) button — default true */
  showCloseButton?: boolean;
  /** Style applied to the portal root — use to forward CSS custom properties (e.g. brand tokens) */
  portalStyle?: CSSProperties;
  /** Element to focus once the drawer is shown. Falls back to the first focusable element. */
  initialFocusRef?: React.RefObject<HTMLElement | null>;
}

// ─── Constants ───

const DRAWER_WIDTH_DEFAULT = 460;
const TRANSITION_MS = 250;

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

// ─── Close Icon (X) — inline SVG ───

function CloseIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M15 5L5 15M5 5l10 10"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ─── Focus Trap Hook ───

function useFocusTrap(containerRef: React.RefObject<HTMLDivElement | null>, active: boolean) {
  useEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (!container) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Tab') return;
      const focusables = container!.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [active, containerRef]);
}

// ─── Body Scroll Lock ───

function useBodyScrollLock(active: boolean) {
  useEffect(() => {
    if (!active) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [active]);
}

// ─── Drawer Component ───

export function Drawer({
  open,
  onOpenChange,
  title,
  description,
  width = DRAWER_WIDTH_DEFAULT,
  children,
  footer,
  badges,
  closeOnBackdrop = true,
  closeOnEsc = true,
  showCloseButton = true,
  portalStyle,
  initialFocusRef,
}: DrawerProps) {
  const drawerRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descId = useId();

  // mounted = portal in DOM; visible = slide-in animation active
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      // Wait two frames so the initial off-screen state renders before animating in
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setVisible(true);
        });
      });
    } else {
      setVisible(false);
      const timer = setTimeout(() => setMounted(false), TRANSITION_MS);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Esc key handler
  useEffect(() => {
    if (!open || !closeOnEsc) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onOpenChange(false);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, closeOnEsc, onOpenChange]);

  // Focus trap (Tab cycling)
  useFocusTrap(drawerRef, open);

  // Initial focus — runs once the drawer has actually rendered + animated in,
  // so the target element is guaranteed to be in the DOM.
  useEffect(() => {
    if (!visible) return;
    const target =
      initialFocusRef?.current ?? drawerRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    target?.focus();
  }, [visible, initialFocusRef]);

  // Body scroll lock
  useBodyScrollLock(open);

  const handleBackdropClick = useCallback(() => {
    if (closeOnBackdrop) onOpenChange(false);
  }, [closeOnBackdrop, onOpenChange]);

  const resolvedWidth = typeof width === 'number' ? `${width}px` : width;

  if (!mounted) return null;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        pointerEvents: visible ? 'auto' : 'none',
        ...portalStyle,
      }}
    >
      {/* Backdrop / Scrim */}
      <div
        onClick={handleBackdropClick}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.2)',
          opacity: visible ? 1 : 0,
          transition: `opacity ${TRANSITION_MS}ms ease-out`,
        }}
      />

      {/* Drawer Panel */}
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={description ? descId : undefined}
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: resolvedWidth,
          maxWidth: '100vw',
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--bg-surface, #ffffff)',
          boxShadow: '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
          transform: visible ? 'translateX(0)' : 'translateX(100%)',
          transition: `transform ${TRANSITION_MS}ms ease-out`,
          overflow: 'hidden',
          fontFamily: 'var(--font-family, Inter, sans-serif)',
        }}
      >
        {/* Header */}
        {(title || showCloseButton) && (
          <div
            style={{
              flexShrink: 0,
              borderBottom: '1px solid var(--border, #e5e7eb)',
              padding: 'var(--sp-4, 16px) var(--sp-6, 24px)',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 'var(--sp-4, 16px)',
            }}
          >
            {/* Title + badges */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2, 8px)' }}>
              {title && (
                <h2
                  id={titleId}
                  style={{
                    margin: 0,
                    fontSize: '20px',
                    fontWeight: 'var(--font-weight-semi, 600)',
                    lineHeight: '28px',
                    letterSpacing: '-0.45px',
                    color: 'var(--text, #111827)',
                  }}
                >
                  {title}
                </h2>
              )}
              {description && (
                <p
                  id={descId}
                  style={{
                    margin: 0,
                    fontSize: 'var(--font-size-sm, 12px)',
                    lineHeight: 'var(--line-height-sm, 16px)',
                    color: 'var(--text-muted, #6b7280)',
                  }}
                >
                  {description}
                </p>
              )}
              {badges && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2, 8px)' }}>
                  {badges}
                </div>
              )}
            </div>

            {/* Close button */}
            {showCloseButton && (
              <button
                aria-label="Close drawer"
                onClick={() => onOpenChange(false)}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 'var(--radius-md, 10px)',
                  border: 'none',
                  background: 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: 'var(--text-muted, #6b7280)',
                  flexShrink: 0,
                  padding: 0,
                }}
              >
                <CloseIcon />
              </button>
            )}
          </div>
        )}

        {/* Body — scrollable */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            minHeight: 0,
          }}
        >
          {children}
        </div>

        {/* Footer — sticky at bottom */}
        {footer && (
          <div
            style={{
              flexShrink: 0,
              borderTop: '1px solid var(--border, #e5e7eb)',
              padding: 'var(--sp-4, 16px) var(--sp-6, 24px)',
              background: 'var(--bg-surface, #ffffff)',
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

// ─── Badge Helper ───

export interface DrawerBadgeProps {
  label: string;
  variant?: 'warning' | 'neutral';
}

export function DrawerBadge({ label, variant = 'neutral' }: DrawerBadgeProps) {
  const styles: Record<string, CSSProperties> = {
    warning: {
      background: 'var(--color-warning-bg, #fef3c7)',
      color: 'var(--color-warning-text, #92400e)',
    },
    neutral: {
      background: 'var(--bg-surface-2, #f3f4f6)',
      color: 'var(--text-muted, #6b7280)',
    },
  };

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 20,
        padding: '0 var(--sp-2, 8px)',
        borderRadius: 'var(--radius-sm, 8px)',
        fontSize: 'var(--font-size-sm, 12px)',
        fontWeight: 'var(--font-weight-medium, 500)',
        lineHeight: 'var(--line-height-sm, 16px)',
        ...styles[variant],
      }}
    >
      {label}
    </span>
  );
}
